import React, { useState, useEffect } from 'react';
import { Users, Shield, UserCheck, Crown, Diamond, Building, Clock, Search, Download, Plus, Mail, Phone, MessageSquare, Star, Calendar, FileText, Settings, Edit, Eye, Trash2, UserPlus, AlertTriangle, CheckCircle, Activity } from 'lucide-react';

const UsersManagement = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Statistiques utilisateurs
  const userStats = {
    totalUsers: 1847,
    newThisMonth: 124,
    administrators: 18,
    fullAccess: true,
    partners: 342,
    serviceProviders: true,
    vipClients: 892,
    activeTenants: true,
    owners: 247,
    villasYachts: true,
    agencies: 78,
    managers: true,
    onlineNow: 270
  };

  // Catégories d'utilisateurs
  const userCategories = [
    { id: 'all', label: 'Tous', count: 1847, icon: Users },
    { id: 'administrators', label: 'Administrateurs', count: 18, icon: Crown },
    { id: 'staff', label: 'Staff PRIVIA', count: 42, icon: UserCheck },
    { id: 'partners', label: 'Partenaires', count: 342, icon: UserCheck },
    { id: 'vip', label: 'Clients VIP', count: 892, icon: Diamond },
    { id: 'owners', label: 'Propriétaires', count: 247, icon: Building },
    { id: 'agencies', label: 'Agences', count: 78, icon: Building },
    { id: 'pending', label: 'En attente', count: 28, icon: Clock }
  ];

  // Administrateurs et staff
  const administrators = [
    {
      id: 'admin-001',
      initials: 'SA',
      name: 'Sébastien Admin',
      email: 'sebastien@priviacollection.com',
      role: '👑 Super Admin',
      permissions: ['Accès total', 'Gestion users', 'Finance'],
      status: 'online',
      lastConnection: 'Maintenant',
      avatar: 'SA'
    },
    {
      id: 'manager-001',
      initials: 'ML',
      name: 'Marie Laurent',
      email: 'marie.l@priviacollection.com',
      role: '📋 Manager Réservations',
      permissions: ['Réservations', 'Clients', 'Planning'],
      status: 'active',
      lastConnection: 'Il y a 2h',
      avatar: 'ML'
    }
  ];

  // Partenaires
  const partners = [
    {
      id: 'partner-001',
      initials: 'LD',
      name: 'Laurent Dubois',
      email: 'Chef Privé - St-Tropez',
      role: '👨‍🍳 Chef',
      services: ['Gastronomique', 'Events'],
      status: 'active',
      performance: { rating: 4.9, services: 156 },
      avatar: 'LD'
    },
    {
      id: 'partner-002',
      initials: 'RC',
      name: 'Riviera Cars',
      email: 'contact@rivieracars.com',
      role: '🚗 Transport',
      services: ['Location luxe', 'Chauffeur'],
      status: 'active',
      performance: { rating: 4.8, services: 234 },
      avatar: 'RC'
    }
  ];

  // Clients VIP
  const vipClients = [
    {
      id: 'client-001',
      initials: 'JD',
      name: 'James Davidson',
      email: 'j.davidson@email.com',
      category: '💎 VIP Diamond',
      history: '12 séjours',
      clientSince: 'Client depuis 2019',
      status: 'active',
      lifetimeValue: '€2.4M lifetime',
      avatar: 'JD'
    }
  ];

  // Propriétaires
  const owners = [
    {
      id: 'owner-001',
      initials: 'PD',
      name: 'Pierre Dumont',
      email: 'p.dumont@email.com',
      properties: '🏡 3 Villas',
      revenue2024: '€1.8M',
      status: 'active',
      occupancyRate: '87% moyenne',
      avatar: 'PD'
    }
  ];

  // Agences
  const agencies = [
    {
      id: 'agency-001',
      initials: 'LV',
      name: 'Luxury Villas Management',
      email: 'contact@lvm-stbarth.com',
      portfolio: '🏠 28 Propriétés',
      team: '8 agents',
      location: 'St-Barth',
      status: 'active',
      performance: '92% occupation moy.',
      avatar: 'LV'
    }
  ];

  // Activités récentes
  const recentActivities = [
    {
      id: 'activity-001',
      type: 'success',
      icon: '✅',
      title: 'Nouveau partenaire ajouté: Elite Security Services',
      details: 'Par Marie Laurent • Service sécurité',
      time: 'Il y a 10 min'
    },
    {
      id: 'activity-002',
      type: 'upgrade',
      icon: '👤',
      title: 'Client VIP upgradé: Sophie Chen → Diamond',
      details: 'Automatique • 10+ réservations',
      time: 'Il y a 1h'
    },
    {
      id: 'activity-003',
      type: 'permission',
      icon: '🔒',
      title: 'Permissions modifiées pour Agent Cannes Office',
      details: 'Accès finances retiré',
      time: 'Il y a 3h'
    },
    {
      id: 'activity-004',
      type: 'security',
      icon: '⚠️',
      title: 'Compte suspendu: Suspicious Activity User',
      details: 'Tentatives de connexion multiples échouées',
      time: 'Il y a 5h'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'bg-green-100 text-green-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online': return '●';
      case 'active': return '●';
      case 'inactive': return '○';
      case 'suspended': return '⚠';
      default: return '○';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'online': return 'En ligne';
      case 'active': return 'Actif';
      case 'inactive': return 'Inactif';
      case 'suspended': return 'Suspendu';
      default: return status;
    }
  };

  const UserRow = ({ user, type }) => (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
            {user.avatar}
          </div>
          <div>
            <div className="font-medium text-gray-900">{user.name}</div>
            <div className="text-sm text-gray-500">{user.email}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-gray-900">{user.role}</span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {type === 'administrators' && (
          <div className="flex flex-wrap gap-1">
            {user.permissions.slice(0, 2).map((perm, index) => (
              <span key={index} className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded text-xs">
                {perm}
              </span>
            ))}
          </div>
        )}
        {type === 'partners' && (
          <div className="flex flex-wrap gap-1">
            {user.services.map((service, index) => (
              <span key={index} className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">
                {service}
              </span>
            ))}
          </div>
        )}
        {type === 'vip' && (
          <div className="text-sm text-gray-900">
            <div>{user.history}</div>
            <div className="text-gray-500">{user.clientSince}</div>
          </div>
        )}
        {type === 'owners' && (
          <div className="text-sm text-gray-900">{user.properties}</div>
        )}
        {type === 'agencies' && (
          <div className="text-sm text-gray-900">
            <div>{user.portfolio}</div>
            <div className="text-gray-500">{user.team} • {user.location}</div>
          </div>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(user.status)}`}>
          <span className="mr-1">{getStatusIcon(user.status)}</span>
          {getStatusText(user.status)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {type === 'administrators' && (
          <span className="text-sm text-gray-900">{user.lastConnection}</span>
        )}
        {type === 'partners' && user.performance && (
          <div className="text-sm text-gray-900">
            <div>⭐ {user.performance.rating}/5</div>
            <div className="text-gray-500">({user.performance.services} services)</div>
          </div>
        )}
        {type === 'vip' && (
          <div className="text-sm font-medium text-green-600">{user.lifetimeValue}</div>
        )}
        {type === 'owners' && (
          <div className="text-sm text-gray-900">
            <div className="font-medium text-green-600">{user.revenue2024}</div>
            <div className="text-gray-500">{user.occupancyRate}</div>
          </div>
        )}
        {type === 'agencies' && (
          <div className="text-sm font-medium text-blue-600">{user.performance}</div>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex items-center space-x-2">
          {type === 'administrators' && (
            <>
              <button className="text-blue-600 hover:text-blue-900">✏️</button>
              <button className="text-gray-600 hover:text-gray-900">🔒</button>
              <button className="text-purple-600 hover:text-purple-900">📊</button>
            </>
          )}
          {type === 'partners' && (
            <>
              <button className="text-blue-600 hover:text-blue-900">✏️</button>
              <button className="text-green-600 hover:text-green-900">📊</button>
              <button className="text-purple-600 hover:text-purple-900">💬</button>
            </>
          )}
          {type === 'vip' && (
            <>
              <button className="text-blue-600 hover:text-blue-900">👁️</button>
              <button className="text-green-600 hover:text-green-900">📧</button>
              <button className="text-purple-600 hover:text-purple-900">📊</button>
            </>
          )}
          {(type === 'owners' || type === 'agencies') && (
            <>
              <button className="text-blue-600 hover:text-blue-900">👁️</button>
              <button className="text-green-600 hover:text-green-900">📊</button>
              <button className="text-purple-600 hover:text-purple-900">💰</button>
            </>
          )}
        </div>
      </td>
    </tr>
  );

  const getCurrentUsers = () => {
    switch (activeTab) {
      case 'administrators': return administrators;
      case 'partners': return partners;
      case 'vip': return vipClients;
      case 'owners': return owners;
      case 'agencies': return agencies;
      default: return [...administrators, ...partners, ...vipClients, ...owners, ...agencies];
    }
  };

  const getCurrentTableHeaders = () => {
    const baseHeaders = ['Utilisateur', 'Rôle'];
    
    switch (activeTab) {
      case 'administrators':
        return [...baseHeaders, 'Permissions', 'Statut', 'Dernière connexion', 'Actions'];
      case 'partners':
        return [...baseHeaders, 'Services', 'Statut', 'Performance', 'Actions'];
      case 'vip':
        return [...baseHeaders, 'Historique', 'Statut', 'Valeur totale', 'Actions'];
      case 'owners':
        return [...baseHeaders, 'Propriétés', 'Statut', 'Revenus 2024', 'Actions'];
      case 'agencies':
        return [...baseHeaders, 'Portfolio', 'Statut', 'Performance', 'Actions'];
      default:
        return [...baseHeaders, 'Détails', 'Statut', 'Info', 'Actions'];
    }
  };

  return (
    <div className="users-management">
      {/* En-tête */}
      <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Gestion des Utilisateurs</h1>
            <p className="text-gray-600 mt-1">Gérez les accès et permissions de tous les utilisateurs de la plateforme PRIVIA</p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <UserPlus size={18} />
              <span>Inviter un utilisateur</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
              <Shield size={18} />
              <span>Gérer les rôles</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50">
              <Activity size={18} />
              <span>Activité récente</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              <Download size={18} />
              <span>Import en masse</span>
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-blue-600" size={24} />
              <span className="text-sm font-medium text-green-600">+{userStats.newThisMonth}</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{userStats.totalUsers.toLocaleString()}</div>
            <div className="text-sm text-gray-600">Total Utilisateurs</div>
            <div className="text-xs text-gray-500">ce mois</div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Crown className="text-purple-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{userStats.administrators}</div>
            <div className="text-sm text-gray-600">Administrateurs</div>
            <div className="text-xs text-gray-500">Accès complet</div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <UserCheck className="text-green-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{userStats.partners}</div>
            <div className="text-sm text-gray-600">Partenaires</div>
            <div className="text-xs text-gray-500">Prestataires services</div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Diamond className="text-orange-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{userStats.onlineNow}</div>
            <div className="text-sm text-gray-600">Connectés</div>
            <div className="text-xs text-gray-500">En ligne maintenant</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Navigation des catégories */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {userCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveTab(category.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
                  activeTab === category.id
                    ? 'bg-yellow-50 border-yellow-300 text-yellow-800'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <category.icon size={16} />
                <span>{category.label}</span>
                <span className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full">
                  {category.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Tableau principal */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {userCategories.find(c => c.id === activeTab)?.label || 'Tous les utilisateurs'}
                  </h3>
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="text"
                        placeholder="Rechercher..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      />
                    </div>
                    {activeTab === 'administrators' && (
                      <button className="text-sm text-blue-600 hover:text-blue-700 px-3 py-2 border border-blue-300 rounded">
                        Gérer les permissions
                      </button>
                    )}
                    {activeTab === 'partners' && (
                      <button className="text-sm text-green-600 hover:text-green-700 px-3 py-2 border border-green-300 rounded">
                        Inviter partenaire
                      </button>
                    )}
                    {(activeTab === 'vip' || activeTab === 'owners' || activeTab === 'agencies') && (
                      <button className="text-sm text-purple-600 hover:text-purple-700 px-3 py-2 border border-purple-300 rounded">
                        {activeTab === 'vip' ? 'Segmentation clients' : 
                         activeTab === 'owners' ? 'Nouveau propriétaire' : 
                         'Nouvelle agence'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {getCurrentTableHeaders().map((header, index) => (
                        <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getCurrentUsers().map((user) => (
                      <UserRow key={user.id} user={user} type={activeTab} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Sidebar activité récente */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Activité Récente</h3>
                <button className="text-sm text-blue-600 hover:text-blue-700">Voir tout</button>
              </div>
              
              <div className="space-y-4">
                {recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className="text-lg">{activity.icon}</div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">{activity.title}</p>
                      <p className="text-xs text-gray-600 mt-1">{activity.details}</p>
                      <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersManagement;